import { fetchWithChatFeedback } from './chatFeedbackRequired';
/**
 * CLS (Clause Logic System) API Functions
 * Handles document upload, parameter extraction, and clause correction
 */

import {
  CLSDocuments,
  CLSUploadResponse,
  CLSExtractParamsResponse,
  CLSExtractParamsResponseNew,
  CLSContractParams,
  CLSCorrectionResult,
  CLSConfirmedFlags,
  CLSFlags,
  CLSFlagsResponse,
} from '../types/cls';

import { extractDetailMessage } from '../utils/userFacingError';

const API_BASE_URL = process.env.REACT_APP_API_URL || '';
const CLS_REQUEST_DETACH_MS = 15_000;
const CLS_RESULT_POLL_MS = 2_000;
const CLS_RESULT_WAIT_MS = 65 * 60_000;
const CLS_TRANSPORT_GENERATION_DISCOVERY_ATTEMPTS = 3;
const CLS_ABORT_GENERATION_DISCOVERY_ATTEMPTS = 60;
const CLS_CAPABILITY_ATTEMPTS = 3;
const CLS_PREFLIGHT_ATTEMPTS = 3;
const CLS_RECONNECT_GET_TIMEOUT_MS = 10_000;

interface CLSProgress {
  done?: boolean;
  messages?: Array<{ timestamp?: string; message?: string }>;
  error?: { message?: string };
  error_code?: string;
  correction_run_generation?: number;
}

interface CLSError extends Error {
  error_code?: string;
  technical_detail?: string;
}

function makeCLSError(
  message: string,
  errorCode?: string,
  technicalDetail?: string
): CLSError {
  const error = new Error(message) as CLSError;
  if (errorCode) error.error_code = errorCode;
  if (technicalDetail) error.technical_detail = technicalDetail;
  return error;
}

function makeCLSWaitError(): CLSError {
  return makeCLSError(
    'The system is temporarily unavailable. Please wait a few minutes and try again.',
    'wait_and_retry'
  );
}

function requireStatusResponse(response: Response): void {
  if (response.status === 401) {
    window.dispatchEvent(new Event('auth-expired'));
    throw makeCLSError('Your session has expired. Please sign in again.', 'sign_in_again');
  }
  if (response.status === 403) {
    throw makeCLSError(
      'You no longer have access to that workflow.',
      'workflow_access_revoked'
    );
  }
  if (!response.ok) {
    throw new Error(`CLS status request failed (${response.status})`);
  }
}

async function fetchCLSReconnectStatus(
  url: string,
  options: RequestInit
): Promise<Response> {
  const controller = new AbortController();
  const timeout = setTimeout(
    () => controller.abort(),
    CLS_RECONNECT_GET_TIMEOUT_MS
  );
  try {
    return await fetchWithChatFeedback(url, { ...options, signal: controller.signal });
  } finally {
    clearTimeout(timeout);
  }
}

async function supportsDurableCLSReconnect(): Promise<boolean> {
  for (let attempt = 0; attempt < CLS_CAPABILITY_ATTEMPTS; attempt += 1) {
    try {
      const response = await fetchCLSReconnectStatus(
        `${API_BASE_URL}/api/cls/runtime-capabilities`,
        { method: 'GET', credentials: 'include' }
      );
      if (response.status === 401) {
        window.dispatchEvent(new Event('auth-expired'));
        return false;
      }
      if (
        response.status === 408
        || response.status === 429
        || response.status >= 500
      ) {
        throw new Error(`CLS capability request failed (${response.status})`);
      }
      if (!response.ok) return false;
      const payload = await response.json();
      return payload?.durable_reconnect === true;
    } catch {
      if (attempt === CLS_CAPABILITY_ATTEMPTS - 1) {
        return false;
      }
    }
  }
  return false;
}

function isCompletedCLSCorrection(value: unknown): value is CLSCorrectionResult {
  if (!value || typeof value !== 'object') return false;
  const result = value as Partial<CLSCorrectionResult>;
  return typeof result.corrected_xml_path === 'string'
    && Array.isArray(result.clauses_to_add)
    && Array.isArray(result.clauses_to_remove)
    && Array.isArray(result.clauses_to_update)
    && Array.isArray(result.clauses_unchanged);
}

function isQuestionsPending(value: unknown): value is CLSCorrectionResult {
  if (!value || typeof value !== 'object') return false;
  const result = value as Partial<CLSCorrectionResult>;
  return result.status === 'questions_pending' && Array.isArray(result.questions);
}

async function getCLSProgress(flowId: string): Promise<CLSProgress | null> {
  const response = await fetchCLSReconnectStatus(
    `${API_BASE_URL}/api/flows/${encodeURIComponent(flowId)}/stage/103/progress`,
    { method: 'GET', credentials: 'include', cache: 'no-store' }
  );
  if (response.status === 404) {
    throw makeCLSError(
      'That workflow could not be loaded.',
      'workflow_unavailable'
    );
  }
  requireStatusResponse(response);
  return await response.json();
}

async function loadCompletedCLSCorrection(
  flowId: string,
  expectedGeneration: number
): Promise<CLSCorrectionResult | null> {
  const response = await fetchCLSReconnectStatus(
    `${API_BASE_URL}/api/cls/${encodeURIComponent(flowId)}/stage/3`,
    { method: 'GET', credentials: 'include', cache: 'no-store' }
  );
  if (response.status === 404) return null;
  requireStatusResponse(response);
  const payload = await response.json();
  return (
    isCompletedCLSCorrection(payload?.data)
    || isQuestionsPending(payload?.data)
  ) && payload.data.correction_run_generation === expectedGeneration
    ? payload.data
    : null;
}

async function waitForCLSCorrection(
  flowId: string,
  expectedGeneration?: number,
  generationBeforeRequest?: number,
  generationDiscoveryLimit?: number
): Promise<CLSCorrectionResult> {
  const deadline = Date.now() + CLS_RESULT_WAIT_MS;
  let consecutiveStatusFailures = 0;
  let generationDiscoveryAttempts = 0;
  let generation = expectedGeneration;
  while (Date.now() < deadline) {
    try {
      const progress = await getCLSProgress(flowId);
      const observedGeneration = progress?.correction_run_generation;
      if (
        generation !== undefined
        && typeof observedGeneration === 'number'
        && observedGeneration > generation
      ) {
        const priorCompleted = await loadCompletedCLSCorrection(
          flowId,
          generation
        );
        if (priorCompleted) return priorCompleted;
        generation = observedGeneration;
      }
      if (
        generation === undefined
        && typeof observedGeneration === 'number'
        && (
          progress?.done === false
          || (
            generationBeforeRequest !== undefined
            && observedGeneration > generationBeforeRequest
          )
        )
      ) {
        generation = observedGeneration;
      }
      if (
        generation === undefined
        && (
          generationBeforeRequest === undefined
          || generationDiscoveryLimit !== undefined
        )
      ) {
        generationDiscoveryAttempts += 1;
        if (
          generationDiscoveryAttempts >= (
            generationDiscoveryLimit
            ?? CLS_TRANSPORT_GENERATION_DISCOVERY_ATTEMPTS
          )
        ) {
          throw makeCLSWaitError();
        }
      }
      if (generation === undefined || observedGeneration !== generation) {
        consecutiveStatusFailures = 0;
      } else {
        const completed = await loadCompletedCLSCorrection(flowId, generation);
        if (completed) return completed;
        if (progress?.done && progress.error) {
          throw makeCLSError(
            progress.error.message || 'Correction failed',
            progress.error_code
          );
        }
      }
      consecutiveStatusFailures = 0;
    } catch (error) {
      if ((error as CLSError)?.error_code) throw error;
      consecutiveStatusFailures += 1;
    }
    const retryDelay = CLS_RESULT_POLL_MS * Math.min(
      2 ** consecutiveStatusFailures,
      5
    );
    await new Promise(resolve => setTimeout(resolve, retryDelay));
  }
  throw makeCLSWaitError();
}

/**
 * Get user email from localStorage for authentication headers
 */
function getUserEmail(): string {
  return localStorage.getItem('userEmail') || (window as any).userEmail || '';
}

/**
 * Upload a document for CLS processing
 * @param flowId - The flow/session identifier
 * @param fileType - Type of file: 'draft_xml', 'final_quote', 'form9', 'sow'
 * @param file - The file to upload
 * @returns Upload response with file path and size
 */
export async function uploadCLSDocument(
  flowId: string,
  fileType: string,
  file: File
): Promise<CLSUploadResponse> {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('flow_id', flowId);
  formData.append('file_type', fileType);

  const headers: Record<string, string> = {};

  const response = await fetchWithChatFeedback(`${API_BASE_URL}/api/cls/upload`, {
    method: 'POST',
    headers,
    body: formData,
    credentials: 'include',
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Upload failed' }));
    const detail = extractDetailMessage(error.detail, `Upload failed with status ${response.status}`);
    throw new Error(detail);
  }

  return response.json();
}

/**
 * Get list of uploaded CLS documents for a flow
 * @param flowId - The flow/session identifier
 * @returns Object containing info about uploaded documents
 */
export async function getCLSDocuments(flowId: string): Promise<CLSDocuments> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };

  const response = await fetchWithChatFeedback(`${API_BASE_URL}/api/cls/${flowId}/documents`, {
    method: 'GET',
    headers,
    credentials: 'include',
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Failed to get documents' }));
    const detail = extractDetailMessage(error.detail, `Failed to get documents with status ${response.status}`);
    throw new Error(detail);
  }

  return response.json();
}

/**
 * Extract parameters from uploaded CLS documents (legacy)
 * @param flowId - The flow/session identifier
 * @returns Extracted parameters and questions for user input
 */
export async function extractCLSParams(flowId: string): Promise<CLSExtractParamsResponse> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };

  const response = await fetchWithChatFeedback(`${API_BASE_URL}/api/cls/extract-params`, {
    method: 'POST',
    headers,
    body: JSON.stringify({ flow_id: flowId }),
    credentials: 'include',
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Parameter extraction failed' }));
    const detail = extractDetailMessage(error.detail, `Parameter extraction failed with status ${response.status}`);
    throw new Error(detail);
  }

  return response.json();
}

/**
 * Extract contract parameters from uploaded CLS documents (new version)
 * @param flowId - The flow/session identifier
 * @returns Contract parameters ready for editing
 */
export async function extractCLSParamsNew(flowId: string): Promise<CLSExtractParamsResponseNew> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };

  const response = await fetchWithChatFeedback(`${API_BASE_URL}/api/cls/extract-params`, {
    method: 'POST',
    headers,
    body: JSON.stringify({ flow_id: flowId }),
    credentials: 'include',
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Parameter extraction failed' }));
    const detail = extractDetailMessage(error.detail, `Parameter extraction failed with status ${response.status}`);
    throw new Error(detail);
  }

  return response.json();
}

/**
 * Extract applicability flags from source documents (Phase 1 of two-phase loading).
 *
 * This is the first phase that runs Stage 0 only to determine applicability flags.
 * Returns flags immediately so the frontend can display Contract Details before
 * running the full prescription evaluation.
 *
 * @param flowId - The flow/session identifier
 * @param userFlagAnswers - Optional dict mapping generated question text to free-text answers
 * @returns Flags result or questions needing user input
 */
export async function extractApplicabilityFlags(
  flowId: string,
  userFlagAnswers?: Record<string, string>
): Promise<CLSFlagsResponse> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };

  const response = await fetchWithChatFeedback(`${API_BASE_URL}/api/cls/flags`, {
    method: 'POST',
    headers,
    body: JSON.stringify({
      flow_id: flowId,
      user_flag_answers: userFlagAnswers ?? null,
    }),
    credentials: 'include',
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Flags extraction failed' }));
    const detail = extractDetailMessage(error.detail, `Flags extraction failed with status ${response.status}`);
    throw new Error(detail);
  }

  return response.json();
}

/** Persist the complete user-reviewed applicability input before clause search. */
export async function confirmApplicabilityFlags(
  flowId: string,
  confirmation: CLSConfirmedFlags
): Promise<{ status: 'flags_confirmed'; flags?: CLSFlags; confirmed_flags?: CLSFlags }> {
  // The API validates this derived Matrix control independently of the
  // complete flags object. Keep the duplicated transport field in sync with
  // the reviewed value rather than trusting a separate UI state value.
  const requestBody = {
    ...confirmation,
    is_under_350k: confirmation.flags.is_under_350k,
  };
  const response = await fetchWithChatFeedback(
    `${API_BASE_URL}/api/cls/${encodeURIComponent(flowId)}/applicability-flags/confirm`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(requestBody),
      credentials: 'include',
    }
  );

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Contract detail confirmation failed' }));
    const detail = extractDetailMessage(error.detail, `Contract detail confirmation failed with status ${response.status}`);
    throw new Error(detail);
  }

  return response.json();
}

/** Explicitly acknowledge the documented broad-evaluation fallback. */
export async function acknowledgeApplicabilityFallback(
  flowId: string
): Promise<{ status: 'fallback_acknowledged' }> {
  const response = await fetchWithChatFeedback(
    `${API_BASE_URL}/api/cls/${encodeURIComponent(flowId)}/applicability-flags/acknowledge-fallback`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
    }
  );
  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Fallback acknowledgement failed' }));
    const detail = extractDetailMessage(error.detail, `Fallback acknowledgement failed with status ${response.status}`);
    throw new Error(detail);
  }
  return response.json();
}

/**
 * Run CLS clause correction on the draft XML (document-first mode)
 * Extracts contract parameters directly from uploaded documents and performs clause correction.
 *
 * TWO-PHASE FLOW:
 * - Phase A: Call without userFlagAnswers, may return questions if uncertain flags exist
 * - Phase B: Call with userFlagAnswers after user responds to questions
 *
 * @param flowId - The flow/session identifier
 * @param skipCompletenessCheck - Whether to skip completeness check (optional, defaults to false)
 * @param userFlagAnswers - Optional dict mapping question text to user answers (yes/no)
 * @param skipCache - Whether to bypass cached flags and regenerate fresh (optional, defaults to false)
 * @returns Correction result with clauses OR questions needing user input
 */
export async function runCLSCorrection(
  flowId: string,
  skipCompletenessCheck?: boolean,
  userFlagAnswers?: Record<string, string>,
  skipCache?: boolean
): Promise<CLSCorrectionResult> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };

  console.log('[runCLSCorrection] Request params:', {
    flow_id: flowId,
    skip_completeness_check: skipCompletenessCheck ?? false,
    user_flag_answers: userFlagAnswers ?? null,
    use_cache: skipCache === undefined ? true : !skipCache,
  });

  let durableReconnect = await supportsDurableCLSReconnect();
  let generationBeforeRequest: number | undefined;
  let reconnectRunGeneration: number | undefined;
  if (durableReconnect) {
    for (let attempt = 0; attempt < CLS_PREFLIGHT_ATTEMPTS; attempt += 1) {
      try {
        const progress = await getCLSProgress(flowId);
        generationBeforeRequest = progress?.correction_run_generation ?? -1;
        if (
          progress?.done === false
          && typeof progress.correction_run_generation === 'number'
        ) {
          reconnectRunGeneration = progress.correction_run_generation;
        }
        break;
      } catch (error) {
        if ((error as CLSError)?.error_code) throw error;
        if (attempt === CLS_PREFLIGHT_ATTEMPTS - 1) {
          durableReconnect = false;
        }
      }
    }
  }
  const controller = durableReconnect ? new AbortController() : null;
  const detachTimer = controller
    ? setTimeout(() => controller.abort(), CLS_REQUEST_DETACH_MS)
    : null;
  let response: Response;
  try {
    response = await fetchWithChatFeedback(`${API_BASE_URL}/api/cls/correct`, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        flow_id: flowId,
        skip_completeness_check: skipCompletenessCheck ?? false,
        user_flag_answers: userFlagAnswers ?? null,
        use_cache: skipCache === undefined ? true : !skipCache,
        reconnect_run_generation: reconnectRunGeneration,
      }),
      credentials: 'include',
      ...(controller ? { signal: controller.signal } : {}),
    });
  } catch (error) {
    if (
      error instanceof Error
      && (
        error.name === 'AbortError'
        || (durableReconnect && error instanceof TypeError)
      )
    ) {
      return waitForCLSCorrection(
        flowId,
        reconnectRunGeneration,
        generationBeforeRequest,
        error.name === 'AbortError'
          ? CLS_ABORT_GENERATION_DISCOVERY_ATTEMPTS
          : CLS_TRANSPORT_GENERATION_DISCOVERY_ATTEMPTS
      );
    }
    throw error;
  } finally {
    if (detachTimer !== null) clearTimeout(detachTimer);
  }

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Correction failed' }));
    if (
      response.status === 409
      && error?.detail?.error_code === 'cls_correction_in_progress'
    ) {
      return waitForCLSCorrection(
        flowId,
        error.detail.correction_run_generation
      );
    }
    if (response.status === 401) {
      window.dispatchEvent(new Event('auth-expired'));
    }
    const detail = extractDetailMessage(error.detail, `Correction failed with status ${response.status}`);
    throw makeCLSError(
      detail,
      error?.detail?.error_code,
      error?.detail?.technical_detail
    );
  }

  const result = await response.json();
  console.log('[runCLSCorrection] Response received:', {
    status: result.status,
    has_questions: result.has_questions,
    questions_count: result.questions?.length || 0,
    dodaac_questions: result.dodaac_questions,
  });
  return result;
}

/**
 * Delete a specific CLS document
 * @param flowId - The flow/session identifier
 * @param fileType - Type of file to delete
 */
export async function deleteCLSDocument(
  flowId: string,
  fileType: string
): Promise<{ success: boolean }> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };

  const response = await fetchWithChatFeedback(`${API_BASE_URL}/api/cls/${flowId}/documents/${fileType}`, {
    method: 'DELETE',
    headers,
    credentials: 'include',
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Delete failed' }));
    const detail = extractDetailMessage(error.detail, `Delete failed with status ${response.status}`);
    throw new Error(detail);
  }

  return response.json();
}

/**
 * Delete a single Form 9 file
 * @param flowId - The flow/session identifier
 * @param filename - Name of the Form 9 file to delete
 */
export async function deleteSingleForm9(
  flowId: string,
  filename: string
): Promise<{ success: boolean }> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };

  const response = await fetchWithChatFeedback(
    `${API_BASE_URL}/api/cls/${flowId}/documents/form9/${filename}`,
    {
      method: 'DELETE',
      headers,
      credentials: 'include',
    }
  );

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Delete failed' }));
    const detail = extractDetailMessage(error.detail, `Delete failed with status ${response.status}`);
    throw new Error(detail);
  }

  return response.json();
}

/**
 * Get cached CLS stage data
 * @param flowId - The flow/session identifier
 * @param stageNum - Stage number (1=upload, 2=extract-params, 3=correct)
 * @returns Cached stage data or null if not found
 */
export async function getCLSStageData(
  flowId: string,
  stageNum: number
): Promise<{ success: boolean; data: any } | null> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };

  try {
    const response = await fetchWithChatFeedback(`${API_BASE_URL}/api/cls/${flowId}/stage/${stageNum}`, {
      method: 'GET',
      headers,
      credentials: 'include',
    });

    if (response.status === 404) {
      // No cached data - this is expected for new flows
      return null;
    }

    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Failed to load stage data' }));
      console.warn('Failed to load CLS stage data:', error.detail);
      return null;
    }

    return response.json();
  } catch (err) {
    console.warn('Error loading CLS stage data:', err);
    return null;
  }
}

/**
 * Save CLS context question answers to cache
 * @param flowId - The flow/session identifier
 * @param questions - Array of questions that were asked
 * @param answers - Record mapping question text to user's answer
 * @returns Success response
 */
export async function saveCLSAnswers(
  flowId: string,
  questions: any[],
  answers: Record<string, string>
): Promise<{ success: boolean; data?: any; error?: string }> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };

  try {
    const response = await fetchWithChatFeedback(`${API_BASE_URL}/api/cls/save-answers`, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        flow_id: flowId,
        questions: questions,
        answers: answers,
      }),
      credentials: 'include',
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Failed to save answers' }));
      const detail = extractDetailMessage(error.detail, `Failed to save answers with status ${response.status}`);
      return { success: false, error: detail };
    }

    const result = await response.json();
    return { success: true, data: result };
  } catch (error: any) {
    console.error('Error saving CLS answers:', error);
    return { success: false, error: error.message || 'Failed to save answers' };
  }
}

/**
 * Get clause details by clause number
 * @param clauseNumber - The clause number to look up (e.g., "52.212-1" or "252.204-7012")
 * @returns Object with found flag and full clause details if found
 */
export async function getClauseByNumber(
  clauseNumber: string
): Promise<{
  found: boolean;
  clause_number?: string;
  title?: string;
  full_text?: string;
  incorporated_by_reference?: boolean;
  has_deviations?: boolean;
  deviation_text?: string;
  prescription_text?: string;
  has_alternates?: boolean;
  alternate_text?: string;
  alternate_texts?: Record<string, string>;
  effective_date?: string;
  url?: string;
}> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };

  const response = await fetchWithChatFeedback(
    `${API_BASE_URL}/api/cls/clause/${encodeURIComponent(clauseNumber)}`,
    {
      method: 'GET',
      headers,
      credentials: 'include',
    }
  );

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Failed to get clause' }));
    const detail = extractDetailMessage(error.detail, `Failed to get clause with status ${response.status}`);
    throw new Error(detail);
  }

  return response.json();
}


/**
 * Save manual clause changes to cache
 */
export async function saveCLSManualChanges(
  flowId: string,
  userOverrides: Record<string, 'added' | 'removed'>,
  hiddenClauses: string[],
  manuallyAddedClauses: any[],
  sectionOverrides: Record<string, string>,
  reviewedClauses: string[] = [],
  userOverrideRationales: Record<string, string> = {},
): Promise<{
  success: boolean;
  message?: string;
  error?: string;
  manualEditMetadata?: Record<string, { user: string; timestamp: string; action: string }>;
}> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };

  try {
    const response = await fetchWithChatFeedback(`${API_BASE_URL}/api/cls/${flowId}/save-manual-changes`, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        userOverrides,
        hiddenClauses,
        manuallyAddedClauses,
        sectionOverrides,
        reviewedClauses,
        userOverrideRationales,
      }),
      credentials: 'include',
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Failed to save manual changes' }));
      const detail = extractDetailMessage(error.detail, `Failed to save manual changes with status ${response.status}`);
      return { success: false, error: detail };
    }

    const result = await response.json();
    return { success: true, message: result.message, manualEditMetadata: result.manualEditMetadata };
  } catch (error: any) {
    console.error('Error saving manual changes:', error);
    return { success: false, error: error.message || 'Failed to save manual changes' };
  }
}

/**
 * Get saved manual clause changes from cache
 */
export async function getCLSManualChanges(flowId: string) {
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
  };

  const response = await fetchWithChatFeedback(
    `${API_BASE_URL}/api/cls/${flowId}/manual-changes`,
    {
      method: 'GET',
      headers,
      credentials: 'include',
    }
  );

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Failed to get manual changes' }));
    const detail = extractDetailMessage(error.detail, `Failed to get manual changes with status ${response.status}`);
    throw new Error(detail);
  }

  return response.json();
}
